// Quote Lifecycle State Machine
// Pattern follows src/lib/kyc/stateMachine.js — Object.freeze with role-gated transitions

import { QUOTE_STATUSES, LP_ROLES } from './constants';

// ─── State Transition Map ───────────────────────────────────────────────
// Each state maps to an array of allowed transitions.
// Transitions can be plain status strings or { state, roles } objects for role-gating.
const TRANSITIONS = Object.freeze({
  [QUOTE_STATUSES.LIVE]: [
    { state: QUOTE_STATUSES.AMENDED, roles: [LP_ROLES.DEALER, LP_ROLES.SENIOR_DEALER] },
    { state: QUOTE_STATUSES.WITHDRAWN, roles: [LP_ROLES.DEALER, LP_ROLES.SENIOR_DEALER] },
    QUOTE_STATUSES.ACCEPTED,   // system-triggered (venue notification)
    QUOTE_STATUSES.REJECTED,   // system-triggered (venue notification)
    QUOTE_STATUSES.EXPIRED,    // system-triggered (timer)
  ],

  [QUOTE_STATUSES.AMENDED]: [
    { state: QUOTE_STATUSES.AMENDED, roles: [LP_ROLES.DEALER, LP_ROLES.SENIOR_DEALER] },
    { state: QUOTE_STATUSES.WITHDRAWN, roles: [LP_ROLES.DEALER, LP_ROLES.SENIOR_DEALER] },
    QUOTE_STATUSES.ACCEPTED,
    QUOTE_STATUSES.REJECTED,
    QUOTE_STATUSES.EXPIRED,
  ],

  // Terminal states — no further transitions
  [QUOTE_STATUSES.WITHDRAWN]: [],
  [QUOTE_STATUSES.ACCEPTED]: [],
  [QUOTE_STATUSES.REJECTED]: [],
  [QUOTE_STATUSES.EXPIRED]: [],
});

// ─── Terminal States ────────────────────────────────────────────────────
const TERMINAL_STATES = Object.freeze(new Set([
  QUOTE_STATUSES.WITHDRAWN,
  QUOTE_STATUSES.ACCEPTED,
  QUOTE_STATUSES.REJECTED,
  QUOTE_STATUSES.EXPIRED,
]));

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Normalise a transition entry to { state, roles }.
 * Plain strings have no role gate (anyone/system can trigger).
 */
const normalise = (entry) =>
  typeof entry === 'string' ? { state: entry, roles: null } : entry;

/**
 * Check whether a transition from `currentState` to `targetState` is defined
 * (ignores role).
 */
export function canTransition(currentState, targetState) {
  const allowed = TRANSITIONS[currentState];
  if (!allowed) return false;
  return allowed.some((t) => normalise(t).state === targetState);
}

/**
 * Return the list of states reachable from `currentState`, optionally
 * filtered by `userRole`.  If `userRole` is omitted, all transitions are
 * returned (including system-only ones).
 */
export function getAvailableTransitions(currentState, userRole) {
  const allowed = TRANSITIONS[currentState];
  if (!allowed) return [];

  return allowed
    .map(normalise)
    .filter(({ roles }) => {
      if (!roles) return true; // no role gate — always available
      if (!userRole) return true; // no role provided — return all
      return roles.includes(userRole);
    })
    .map(({ state }) => state);
}

/**
 * Validate that a transition is legal and the user has the right role.
 * Returns { valid: true } or { valid: false, reason: string }.
 */
export function validateTransition(currentState, targetState, userRole) {
  if (TERMINAL_STATES.has(currentState)) {
    return { valid: false, reason: `Quote is in terminal state "${currentState}" and cannot transition.` };
  }

  const allowed = TRANSITIONS[currentState];
  if (!allowed) {
    return { valid: false, reason: `Unknown quote state "${currentState}".` };
  }

  const match = allowed.find((t) => normalise(t).state === targetState);
  if (!match) {
    return { valid: false, reason: `Transition from "${currentState}" to "${targetState}" is not defined.` };
  }

  const { roles } = normalise(match);
  if (roles && userRole && !roles.includes(userRole)) {
    return {
      valid: false,
      reason: `Role "${userRole}" is not authorised for transition from "${currentState}" to "${targetState}". Required: ${roles.join(', ')}.`,
    };
  }

  return { valid: true };
}

/**
 * Check whether a quote is in a terminal (non-actionable) state.
 */
export function isTerminal(state) {
  return TERMINAL_STATES.has(state);
}

/**
 * Check whether a quote is still actionable (live or amended).
 */
export function isActionable(state) {
  return state === QUOTE_STATUSES.LIVE || state === QUOTE_STATUSES.AMENDED;
}

export default {
  canTransition,
  getAvailableTransitions,
  validateTransition,
  isTerminal,
  isActionable,
  TRANSITIONS,
  TERMINAL_STATES,
};
